<template>
  <div class="max-w-4xl mx-auto space-y-6">
    <!-- Page header -->
    <div class="flex items-center gap-3">
      <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
        <span class="material-icons-round">person_add</span>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-800">Add Driver</h2>
        <p class="text-sm text-gray-500">Create a new driver profile and upload their documents.</p>
      </div>
    </div>

    <form @submit.prevent class="space-y-6">
      <!-- Basic information -->
      <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div class="flex items-center mb-5 space-x-1 text-gray-500">
          <span class="text-base material-icons-round">badge</span>
          <span class="text-xs font-semibold tracking-widest uppercase">Basic Information</span>
        </div>

        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">First Name</span>
            <input
              v-model="driver.firstName"
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="Jane"
            />
          </label>
          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Last Name</span>
            <input
              v-model="driver.lastName"
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="Doe"
            />
          </label>
          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Taxiye ID</span>
            <input
              v-model="driver.driverId"
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="taxiye ID"
            />
          </label>
          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">City</span>
            <input
              v-model="driver.city"
              type="text"
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="Addis Ababa"
            />
          </label>
          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Email</span>
            <input
              v-model="driver.email"
              type="email"
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="johndoe@gmail.com"
            />
          </label>
          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Contact Number</span>
            <input
              v-model="driver.phoneNumber"
              type="text"
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="+251 912345678"
            />
          </label>
          <label class="block sm:col-span-2">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Vehicle Type</span>
            <select class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300">
              <option
                v-for="type in vehicleTypes"
                :value="type._id"
                :key="`vType-${type._id}`"
              >{{ type.name }}</option>
            </select>
          </label>
        </div>

        <label class="flex items-center gap-3 mt-5 cursor-pointer">
          <input type="checkbox" class="w-5 h-5 rounded form-checkbox text-taxiye-500" v-model="driver.isEmployee" />
          <span class="text-sm text-gray-700">This driver is an employee</span>
        </label>
      </div>

      <!-- Documents -->
      <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div class="flex items-center mb-5 space-x-1 text-gray-500">
          <span class="text-base material-icons-round">description</span>
          <span class="text-xs font-semibold tracking-widest uppercase">Documents</span>
        </div>

        <div class="flex flex-wrap gap-8">
          <div>
            <span class="block mb-2 text-xs font-semibold tracking-wider text-gray-600 uppercase">Profile Picture</span>
            <photo-uploader v-model="driver.profileImage" label="" />
          </div>
          <div>
            <span class="block mb-2 text-xs font-semibold tracking-wider text-gray-600 uppercase">Business License</span>
            <photo-uploader v-model="driver.businessLicense" label="" />
          </div>
          <div>
            <span class="block mb-2 text-xs font-semibold tracking-wider text-gray-600 uppercase">Driver's License</span>
            <photo-uploader v-model="driver.drivingLicense" label="" />
          </div>
        </div>

        <div class="mt-6">
          <span class="block mb-3 text-xs font-semibold tracking-wider text-gray-600 uppercase">Additional Document(s)</span>
          <div class="flex flex-wrap gap-8">
            <photo-uploader
              v-for="(i, index) in driver.additionalDocuments"
              :key="`additional-document-${index}`"
              :value="driver.additionalDocuments[index]"
              label=""
              @input="driver.additionalDocuments.splice(index, 1)"
            />
            <photo-uploader label="" @input="x => driver.additionalDocuments.push(x)" />
          </div>
        </div>

        <label class="flex items-center gap-3 mt-6 cursor-pointer">
          <input type="checkbox" class="w-5 h-5 rounded form-checkbox text-taxiye-500" v-model="driver.representative" />
          <span class="text-sm text-gray-700">Representative (Optional)</span>
        </label>
        <div v-if="driver.representative" class="mt-4">
          <span class="block mb-2 text-xs font-semibold tracking-wider text-gray-600 uppercase">Representation Paper</span>
          <photo-uploader v-model="driver.representationPaper" label="" />
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center justify-end gap-3">
        <back-button></back-button>
        <button
          class="inline-flex items-center h-10 px-5 text-sm font-semibold text-white shadow-sm rounded-xl bg-taxiye-500 transition-colors duration-150 hover:bg-taxiye-600"
          @click="add"
        >
          <span class="mr-1 text-base material-icons-round">check</span>
          Add Driver
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import PhotoUploader from "@/components/PhotoUploader.vue";
import BackButton from "@/components/BackButton.vue";

export default {
  components: {
    PhotoUploader,
    BackButton,
  },
  data() {
    return {
      driver: {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        profileImage: "",
        city: "",
        vehicleType: "",
        businessLicense: "",
        drivingLicense: "",
        representationPaper: "",
        additionalDocuments: [],
        driverId: "",
      },
      vehicleTypes: [],
    };
  },
  created() {
    this.getVehicleTypes();
  },
  methods: {
    getVehicleTypes() {
      this.$misc
        .get("vehicleTypes")
        .then(({ data }) => (this.vehicleTypes = data.data))
        .catch(err => console.log(err));
    },
    add() {
      this.$misc
        .post("drivers", this.driver)
        .then(() => {
          window.flash({
            type: "success",
            body: "Driver added successfully!",
          });
          this.$router.push("/drivers");
        })
        .catch(err => {
          if (err.response && err.response.data && err.response.data.message && err.response.data.message.includes('validation failed')) {
            window.flash({
              type: "error",
              body: err.response.data.message.split(':').slice(1).join(':').split(',').join('<br/>'),
            });
            console.log(err);
          } else {
            window.flash({
              type: "error",
              body: "Failed to add the driver!",
            });
            console.log(err);
          }
        });
    },
  },
};
</script>
